import React, { Component } from 'react'
import { func, shape, string, number, arrayOf, bool } from 'prop-types'
import { translate } from 'react-i18next'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { isValidPricing, getPricingValues, formatPricingAfterTax } from '../../utilities/gardener-seo'
import { getReviews } from '../../api/gardeners'
import { ZipCodesToCities } from '../../api/externals'
import { sanitizeZipCodes } from '../../utilities/validators'
import { PagesPaths } from '../../constants/Pages'
import config from '../../config'
import ZoneMap from './ZoneMap'
import './GardenerDetail.css'

const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
)

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
)

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
)

const ZoneCheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#009d3d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

class GardenerDetailComponent extends Component {
  constructor (props) {
    super(props)
    this.state = {
      reviewsData: null,
      reviewsLoading: false,
      reviewsExpanded: false,
      lightboxOpen: false,
      lightboxIndex: 0,
      zoneCities: {}
    }
    this.handleCopyLink = this.handleCopyLink.bind(this)
    this.handleShare = this.handleShare.bind(this)
    this.handleShowMoreReviews = this.handleShowMoreReviews.bind(this)
    this.openLightbox = this.openLightbox.bind(this)
    this.closeLightbox = this.closeLightbox.bind(this)
    this.lightboxPrev = this.lightboxPrev.bind(this)
    this.lightboxNext = this.lightboxNext.bind(this)
    this.handleLightboxKey = this.handleLightboxKey.bind(this)
  }

  componentDidMount () {
    this.fetchReviews()
    this.resolveZoneCities()
  }

  componentDidUpdate (prevProps) {
    var gardener = this.props.gardener || {}
    var prevGardener = prevProps.gardener || {}
    if (gardener.entityRelationId && gardener.entityRelationId !== prevGardener.entityRelationId) {
      this.fetchReviews()
    }
    if (this.zoneZipCodes().join(',') !== sanitizeZipCodes(prevGardener.zipCodes).join(',')) {
      this.resolveZoneCities()
    }
  }

  zoneZipCodes () {
    return sanitizeZipCodes((this.props.gardener || {}).zipCodes)
  }

  resolveZoneCities () {
    var zipCodes = this.zoneZipCodes()
    if (!zipCodes.length) return
    var self = this
    ZipCodesToCities(zipCodes)
      .then(function (cities) {
        if (self._unmounted) return
        self.setState({ zoneCities: cities })
      })
      .catch(function () {
        // Cities unavailable — chips stay plain text instead of linking to a wrong page
      })
  }

  fetchReviews () {
    var gardener = this.props.gardener || {}
    if (!gardener.entityRelationId) return
    // DEV/STAGING: use a known entityRelationId with reviews for testing
    var reviewId = config.environment === 'production'
      ? gardener.entityRelationId
      : '5f638b5b3b71787ccdbe5d75'
    var self = this
    self.setState({ reviewsLoading: true })
    getReviews({ entityRelationId: reviewId })
      .then(function (data) {
        self.setState({ reviewsData: data, reviewsLoading: false })
      })
      .catch(function () {
        self.setState({ reviewsData: null, reviewsLoading: false })
      })
  }

  handleCopyLink () {
    var gardener = this.props.gardener || {}
    var url = window.location.origin + PagesPaths.GARDENER.path + '/' + (gardener.externalId || gardener.id)
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
    }
  }

  handleShowMoreReviews () {
    this.setState({ reviewsExpanded: true })
  }

  componentWillUnmount () {
    this._unmounted = true
    document.removeEventListener('keydown', this.handleLightboxKey)
    document.body.style.overflow = ''
  }

  openLightbox (index) {
    this.setState({ lightboxOpen: true, lightboxIndex: index })
    document.addEventListener('keydown', this.handleLightboxKey)
    document.body.style.overflow = 'hidden'
  }

  closeLightbox () {
    this.setState({ lightboxOpen: false })
    document.removeEventListener('keydown', this.handleLightboxKey)
    document.body.style.overflow = ''
  }

  lightboxPrev () {
    var realisations = (this.props.gardener || {}).realisations || []
    this.setState(function (state) {
      return { lightboxIndex: (state.lightboxIndex - 1 + realisations.length) % realisations.length }
    })
  }

  lightboxNext () {
    var realisations = (this.props.gardener || {}).realisations || []
    this.setState(function (state) {
      return { lightboxIndex: (state.lightboxIndex + 1) % realisations.length }
    })
  }

  handleLightboxKey (e) {
    if (e.key === 'Escape') this.closeLightbox()
    if (e.key === 'ArrowLeft') this.lightboxPrev()
    if (e.key === 'ArrowRight') this.lightboxNext()
  }

  handleShare () {
    var gardener = this.props.gardener || {}
    var name = (gardener.name || '').trim()
    if (navigator.share) {
      navigator.share({
        title: name + ' - CMonJardinier',
        url: window.location.href
      })
    }
  }

  handleCtaClick () {
    this.props.history.push(PagesPaths.INQUIRY.path)
  }

  renderHeader () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var name = (gardener.name || '').trim()
    var isPremium = gardener.premium || gardener.isPremium
    var city = this.props.city
    var postalCode = this.props.postalCode
    var since = gardener.since
    var interventionsCount = gardener.interventionsCount
    var profilePicture = gardener.profilePicture

    var avatarClass = 'gardener-detail__avatar'
    if (isPremium) avatarClass += ' gardener-detail__avatar--premium'

    var placeholderClass = 'gardener-detail__avatar-placeholder'
    if (isPremium) placeholderClass += ' gardener-detail__avatar-placeholder--premium'

    var initial = name ? name.charAt(0).toUpperCase() : '?'

    return (
      <div className="gardener-detail__header">
        {profilePicture && profilePicture.path
          ? <img className={avatarClass} src={profilePicture.path} alt={name + ', jardinier à ' + city} />
          : <div className={placeholderClass}>{initial}</div>
        }
        <div className="gardener-detail__header-info">
          <h1 className="gardener-detail__name">{name}</h1>
          {isPremium && (
            <div><span className="gardener-detail__badge-premium">{'\u2B50'} {t('common:jardiniers:detail_premium_badge')}</span></div>
          )}
          <div className="gardener-detail__location">
            <b>{city} ({postalCode})</b>
          </div>
          <div className="gardener-detail__stats">
            {since && (
              <div className="gardener-detail__stat">
                <svg className="gardener-detail__stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {since} {t('common:jardiniers:since_year')} d'expérience
              </div>
            )}
            {since && interventionsCount > 0 && <div className="gardener-detail__stat-divider" />}
            {interventionsCount > 0 && (
              <div className="gardener-detail__stat">
                <svg className="gardener-detail__stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {interventionsCount} interventions
              </div>
            )}
          </div>
          <div className="gardener-detail__share">
            <button className="gardener-detail__share-btn" title={t('common:jardiniers:detail_share')} onClick={this.handleShare}>
              <ShareIcon />
            </button>
            <button className="gardener-detail__share-btn" title={t('common:jardiniers:detail_copy_link')} onClick={this.handleCopyLink}>
              <LinkIcon />
            </button>
          </div>
        </div>
      </div>
    )
  }

  renderSpecialties () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var services = gardener.proposedServices || []
    if (services.length === 0) return null
    return (
      <div className="gardener-detail__section">
        <h2 className="gardener-detail__section-title">{t('common:jardiniers:detail_specialties')}</h2>
        <div className="gardener-detail__tags">
          {services.map(function (service, i) {
            return <span key={i} className="gardener-detail__tag">{service}</span>
          })}
        </div>
      </div>
    )
  }

  renderStars (rate) {
    var stars = []
    for (var i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rate ? 'gardener-detail__star--filled' : 'gardener-detail__star--empty'}>
          {'\u2605'}
        </span>
      )
    }
    return <span className="gardener-detail__stars-row">{stars}</span>
  }

  renderRatingsSummary () {
    var data = this.state.reviewsData
    if (!data || !data.ratings || !data.ratings.count) return null
    var ratings = data.ratings
    var distribution = ratings.distribution || {}
    var maxCount = Math.max(distribution['5'] || 0, distribution['4'] || 0, distribution['3'] || 0, distribution['2'] || 0, distribution['1'] || 0, 1)

    return (
      <div className="gardener-detail__ratings-summary">
        <div className="gardener-detail__ratings-big">
          <div className="gardener-detail__ratings-number">{ratings.average.toFixed(1)}</div>
          <div>{this.renderStars(Math.round(ratings.average))}</div>
          <div className="gardener-detail__ratings-count">{ratings.count} avis</div>
        </div>
        <div className="gardener-detail__ratings-bars">
          {[5, 4, 3, 2, 1].map(function (level) {
            var count = distribution[String(level)] || 0
            var pct = (count / maxCount) * 100
            return (
              <div key={level} className="gardener-detail__ratings-bar-row">
                <span className="gardener-detail__ratings-bar-label">{level}</span>
                <div className="gardener-detail__ratings-bar-track">
                  <div className="gardener-detail__ratings-bar-fill" style={{ width: pct + '%' }} />
                </div>
                <span className="gardener-detail__ratings-bar-count">{count}</span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  renderReviews () {
    var t = this.props.t
    var data = this.state.reviewsData
    var loading = this.state.reviewsLoading
    var reviews = (data && data.reviews) || []
    var hasRatings = data && data.ratings && data.ratings.count > 0

    return (
      <div className="gardener-detail__section">
        <h2 className="gardener-detail__section-title">{t('common:jardiniers:title_reviews')}</h2>
        {loading && <p className="gardener-detail__reviews-loading">Chargement des avis...</p>}
        {!loading && hasRatings && this.renderRatingsSummary()}
        {!loading && reviews.length > 0 && (
          <div className="gardener-detail__reviews-list">
            {(this.state.reviewsExpanded ? reviews : reviews.slice(0, 3)).map(function (review) {
              var date = new Date(review.date)
              var dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              return (
                <div key={review.id} className="gardener-detail__review">
                  <div className="gardener-detail__review-header">
                    <div className="gardener-detail__review-author">
                      <div className="gardener-detail__review-avatar">{(review.author || '?').charAt(0)}</div>
                      <div>
                        <div className="gardener-detail__review-name">{review.author}</div>
                        <div className="gardener-detail__review-date">{dateStr}</div>
                      </div>
                    </div>
                    <div className="gardener-detail__review-stars">
                      {[1, 2, 3, 4, 5].map(function (i) {
                        return <span key={i} className={i <= review.rate ? 'gardener-detail__star--filled' : 'gardener-detail__star--empty'}>{'\u2605'}</span>
                      })}
                    </div>
                  </div>
                  <p className="gardener-detail__review-content">{review.content}</p>
                  {review.verified && (
                    <span className="gardener-detail__review-verified">Avis vérifié</span>
                  )}
                  {review.moderations && review.moderations.length > 0 && (
                    <div className="gardener-detail__review-comments">
                      {review.moderations.map(function (mod, j) {
                        var modDate = mod.date ? new Date(mod.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
                        var label = mod.origin_type === 'merchant' ? 'Réponse du professionnel' : mod.origin_type === 'moderator' ? 'Modérateur' : 'Client'
                        return (
                          <div key={j} className="gardener-detail__review-comment">
                            <div className="gardener-detail__review-comment-header">
                              <span className="gardener-detail__review-comment-label">{label}</span>
                              {modDate && <span className="gardener-detail__review-comment-date">{modDate}</span>}
                            </div>
                            <p className="gardener-detail__review-comment-text">{mod.comment}</p>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            {!this.state.reviewsExpanded && reviews.length > 3 && (
              <button className="gardener-detail__reviews-show-more" onClick={this.handleShowMoreReviews}>
                Voir les {reviews.length - 3} autres avis
              </button>
            )}
          </div>
        )}
        {!loading && !hasRatings && <p>{t('common:jardiniers:empty_reviews')}</p>}
      </div>
    )
  }

  renderDescription () {
    var gardener = this.props.gardener || {}
    if (!gardener.description) return null
    return (
      <div className="gardener-detail__section">
        <h2 className="gardener-detail__section-title">Présentation</h2>
        <div className="gardener-detail__description" dangerouslySetInnerHTML={{ __html: gardener.description }} />
      </div>
    )
  }

  renderLightbox () {
    if (!this.state.lightboxOpen) return null
    var realisations = (this.props.gardener || {}).realisations || []
    var current = realisations[this.state.lightboxIndex]
    if (!current) return null
    var isVideo = current.contentType && current.contentType.indexOf('video') !== -1
    var total = realisations.length
    var index = this.state.lightboxIndex

    return (
      <div className="gardener-detail__lightbox" onClick={this.closeLightbox}>
        <div className="gardener-detail__lightbox-content" onClick={function (e) { e.stopPropagation() }}>
          {isVideo
            ? <video className="gardener-detail__lightbox-media" src={current.path} controls autoPlay />
            : <img className="gardener-detail__lightbox-media" src={current.path} alt={'Réalisation ' + (index + 1)} />
          }
          {total > 1 && (
            <button className="gardener-detail__lightbox-nav gardener-detail__lightbox-nav--prev" onClick={this.lightboxPrev} aria-label="Photo précédente">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}
          {total > 1 && (
            <button className="gardener-detail__lightbox-nav gardener-detail__lightbox-nav--next" onClick={this.lightboxNext} aria-label="Photo suivante">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          )}
          <div className="gardener-detail__lightbox-counter">{(index + 1) + ' / ' + total}</div>
        </div>
        <button className="gardener-detail__lightbox-close" onClick={this.closeLightbox} aria-label="Fermer">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
    )
  }

  renderRealisations () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var name = (gardener.name || '').trim()
    var realisations = gardener.realisations || []
    var self = this

    if (realisations.length === 0) {
      return (
        <div className="gardener-detail__section">
          <h2 className="gardener-detail__section-title">{t('common:jardiniers:title_real')}</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>{t('common:jardiniers:empty_realisations')}</p>
        </div>
      )
    }

    return (
      <div className="gardener-detail__section">
        <h2 className="gardener-detail__section-title">{t('common:jardiniers:title_real')}</h2>
        <div className={'gardener-detail__gallery gardener-detail__gallery--count-' + Math.min(realisations.length, 5)}>
          {realisations.slice(0, 5).map(function (real, i) {
            var isVideo = real.contentType && real.contentType.indexOf('video') !== -1
            var isLast = i === 4 && realisations.length > 5
            return (
              <div key={real.id || i} className="gardener-detail__gallery-item" onClick={function () { self.openLightbox(i) }}>
                {isVideo
                  ? <video src={real.path} loading="lazy" muted />
                  : <img src={real.path} alt={name + ' réalisation ' + (i + 1)} loading="lazy" />
                }
                {i === 0 && realisations.length > 1 && (
                  <div className="gardener-detail__gallery-count">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                    {realisations.length + ' photos'}
                  </div>
                )}
                {isLast && (
                  <div className="gardener-detail__gallery-more">
                    +{realisations.length - 5}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {this.renderLightbox()}
      </div>
    )
  }

  renderCtaCard () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var pricing = getPricingValues(gardener.pricing)
    var pricingValid = isValidPricing(gardener.pricing)

    return (
      <div className="gardener-detail__scard">
        <button className="gardener-detail__cta" onClick={() => this.handleCtaClick()}>
          {t('common:jardiniers:detail_cta')}
        </button>
        <div className="gardener-detail__cta-sub">{t('common:jardiniers:detail_cta_sub')}</div>
        <div className="gardener-detail__sdiv" />
        <div className="gardener-detail__stitle">{t('common:jardiniers:detail_hourly_rate')}</div>
        {pricingValid && pricing ? (
          <div className="gardener-detail__pricing-block">
            <span className="gardener-detail__pricing-old">{pricing.min}-{pricing.max}{'\u20AC'}/h</span>
            <span className="gardener-detail__pricing-new">{formatPricingAfterTax(pricing.min)}-{formatPricingAfterTax(pricing.max)}{'\u20AC'}/h</span>
            <span className="gardener-detail__pricing-credit">{t('common:jardiniers:detail_after_tax_credit')}</span>
          </div>
        ) : (
          <div className="gardener-detail__pricing-on-quote">{t('common:jardiniers:detail_rate_on_quote')}</div>
        )}
      </div>
    )
  }

  renderCredentialsCard () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var certifications = gardener.certifications || []
    var agreementNumber = gardener.agreementNumber

    if (certifications.length === 0 && !agreementNumber) {
      return null
    }

    return (
      <div className="gardener-detail__scard">
        <div className="gardener-detail__stitle">{t('common:jardiniers:detail_certifications')}</div>
        {certifications.map(function (cert, i) {
          return (
            <div key={i} className="gardener-detail__cert">
              <div className="gardener-detail__cert-icon"><CheckIcon /></div>
              <div className="gardener-detail__cert-text">{cert}</div>
            </div>
          )
        })}
        {agreementNumber && (
          <div className="gardener-detail__cert">
            <div className="gardener-detail__cert-icon"><CheckIcon /></div>
            <div className="gardener-detail__cert-text">{'Agrément SAP'} <span>{agreementNumber}</span></div>
          </div>
        )}
      </div>
    )
  }

  renderZoneCard () {
    var gardener = this.props.gardener || {}
    var t = this.props.t
    var name = (gardener.name || '').trim()
    var zipCodes = this.zoneZipCodes()
    var zoneCities = this.state.zoneCities
    var postalCode = (this.props.postalCode || '').toString().trim()

    if (zipCodes.length === 0) return null

    return (
      <div className="gardener-detail__scard">
        <div className="gardener-detail__stitle">{t('common:jardiniers:detail_intervention_zone')}</div>
        <div className="gardener-detail__zone-map">
          <ZoneMap zipCodes={zipCodes} />
        </div>
        <div className="gardener-detail__zone-chips">
          {zipCodes.map(function (zip, i) {
            // Each zip code has its own commune: the listing link must use it,
            // otherwise it points to a page that does not exist for that code
            var zipCity = zoneCities[zip]
            if (!zipCity) {
              return <span key={i} className="gardener-detail__zone-chip">{zip}</span>
            }
            return (
              <Link
                key={i}
                className="gardener-detail__zone-chip"
                to={`${PagesPaths.GARDENERS.path}/${encodeURIComponent(zipCity)}/${zip}`}
                title={'Jardiniers à ' + zipCity + ' (' + zip + ')'}
              >
                {zip}
              </Link>
            )
          })}
        </div>
        {postalCode && zipCodes.indexOf(postalCode) !== -1 && (
          <div className="gardener-detail__zone-confirmed">
            <ZoneCheckIcon />
            <span>{t('common:jardiniers:detail_zone_confirmed', { name: name })}</span>
          </div>
        )}
      </div>
    )
  }

  renderGuaranteesCard () {
    var t = this.props.t
    return (
      <div className="gardener-detail__scard">
        <div className="gardener-detail__stitle">{t('common:jardiniers:detail_guarantees_title')}</div>
        <div className="gardener-detail__guarantee">{t('common:jardiniers:detail_guarantee_verified')}</div>
        <div className="gardener-detail__guarantee">{t('common:jardiniers:detail_guarantee_tax_credit')}</div>
        <div className="gardener-detail__guarantee">{t('common:jardiniers:detail_guarantee_urssaf')}</div>
        <div className="gardener-detail__guarantee">{t('common:jardiniers:detail_guarantee_cesu')}</div>
        <div className="gardener-detail__guarantee">{t('common:jardiniers:detail_guarantee_support')}</div>
      </div>
    )
  }

  renderMobileBar () {
    var gardener = this.props.gardener || {}
    var name = (gardener.name || '').trim()
    return (
      <div className="gardener-detail__mobile-bar">
        <button className="gardener-detail__cta" onClick={() => this.handleCtaClick()}>
          {'Demander un devis gratuit à ' + name}
        </button>
      </div>
    )
  }

  render () {
    return (
      <div className="gardener-detail">
        <div className="gardener-detail__layout">
          <div className="gardener-detail__main">
            {this.renderHeader()}
            <div className="gardener-detail__sep" />
            {this.renderDescription()}
            <div className="gardener-detail__sep" />
            {this.renderRealisations()}
            <div className="gardener-detail__sep" />
            {this.renderSpecialties()}
            <div className="gardener-detail__sep" />
            {this.renderReviews()}
          </div>
          <div className="gardener-detail__side">
            {this.renderCtaCard()}
            {this.renderCredentialsCard()}
            {this.renderZoneCard()}
            {this.renderGuaranteesCard()}
          </div>
        </div>
        {this.renderMobileBar()}
      </div>
    )
  }
}

GardenerDetailComponent.propTypes = {
  t: func,
  history: func,
  postalCode: string,
  city: string,
  gardener: shape({
    entityRelationId: string,
    externalId: string,
    id: string,
    profilePicture: shape({ path: string }),
    name: string,
    since: number,
    description: string,
    premium: bool,
    isPremium: bool,
    proposedServices: arrayOf(string),
    certifications: arrayOf(string),
    interventionsCount: number,
    agreementNumber: string,
    zipCodes: arrayOf(string),
    pricing: shape({
      from: number,
      to: number,
      minimum: number,
      maximum: number
    }),
    realisations: arrayOf(shape({
      id: string,
      path: string,
      contentType: string
    }))
  })
}

export const GardenerDetail = withRouter(translate()(GardenerDetailComponent))



// WEBPACK FOOTER //
// ./src/components/gardeners/GardenerDetail.js